import React, { useEffect, useReducer, useState } from "react";
import openSocket from "../../services/socket-io";
import {
  Button, IconButton, makeStyles, Paper, Table, TableBody,
  TableCell, TableHead, TableRow, Typography,
} from "@material-ui/core";
import { DeleteOutline, Edit } from "@material-ui/icons";
import { toast } from "react-toastify";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import Title from "../../components/Title";
import ConfirmationModal from "../../components/ConfirmationModal";
import LabelModal from "../../components/LabelModal";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  colorDot: {
    width: 24,
    height: 24,
    borderRadius: "50%",
    display: "inline-block",
    border: "1px solid #ccc",
  },
}));

const reducer = (state, action) => {
  if (action.type === "LOAD_LABELS") {
    const incoming = action.payload;
    const next = [...state];
    incoming.forEach(label => {
      const idx = next.findIndex(l => l.id === label.id);
      if (idx !== -1) next[idx] = label;
      else next.push(label);
    });
    return next;
  }
  if (action.type === "UPDATE_LABEL") {
    const label = action.payload;
    const idx = state.findIndex(l => l.id === label.id);
    if (idx !== -1) { state[idx] = label; return [...state]; }
    return [label, ...state];
  }
  if (action.type === "DELETE_LABEL") {
    return state.filter(l => l.id !== action.payload);
  }
  return state;
};

const Labels = () => {
  const classes = useStyles();
  const [labels, dispatch] = useReducer(reducer, []);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deletingLabel, setDeletingLabel] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get("/labels").then(({ data }) => {
      dispatch({ type: "LOAD_LABELS", payload: data });
      setLoading(false);
    }).catch(err => { toastError(err); setLoading(false); });
  }, []);

  useEffect(() => {
    const socket = openSocket();
    socket.on("label", data => {
      if (data.action === "create" || data.action === "update")
        dispatch({ type: "UPDATE_LABEL", payload: data.label });
      if (data.action === "delete")
        dispatch({ type: "DELETE_LABEL", payload: data.labelId });
    });
    return () => socket.disconnect();
  }, []);

  const handleEdit = label => { setSelectedLabel(label); setModalOpen(true); };
  const handleDelete = async () => {
    try {
      await api.delete(`/labels/${deletingLabel.id}`);
      toast.success("Etiqueta removida!");
    } catch (err) { toastError(err); }
    setDeletingLabel(null);
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={deletingLabel && `Excluir etiqueta "${deletingLabel.name}"?`}
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleDelete}
      >
        Esta ação não pode ser desfeita.
      </ConfirmationModal>
      <LabelModal
        open={modalOpen}
        onClose={() => { setModalOpen(false); setSelectedLabel(null); }}
        labelId={selectedLabel?.id}
      />
      <MainHeader>
        <Title>Etiquetas</Title>
        <MainHeaderButtonsWrapper>
          <Button variant="contained" color="primary" onClick={() => setModalOpen(true)}>
            Adicionar Etiqueta
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper className={classes.mainPaper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Cor</TableCell>
              <TableCell>Nome</TableCell>
              <TableCell align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {labels.map(label => (
              <TableRow key={label.id}>
                <TableCell>
                  <span className={classes.colorDot} style={{ backgroundColor: label.color }} />
                </TableCell>
                <TableCell>{label.name}</TableCell>
                <TableCell align="center">
                  <IconButton size="small" onClick={() => handleEdit(label)}>
                    <Edit />
                  </IconButton>
                  <IconButton size="small" onClick={() => { setDeletingLabel(label); setConfirmOpen(true); }}>
                    <DeleteOutline />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {loading && <TableRowSkeleton columns={3} />}
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default Labels;
